import { errorSuffix } from '@caresend/utils';
import { SnackbarProgrammatic } from 'buefy';
import VueRouter from 'vue-router';
import { toastError } from '../../functions/toast/toast.js';
import { getStore } from '../../store/helpers/initStore.js';

var NavigationFailureType;
(function (NavigationFailureType) {
    NavigationFailureType[NavigationFailureType["redirected"] = 2] = "redirected";
    NavigationFailureType[NavigationFailureType["aborted"] = 4] = "aborted";
    NavigationFailureType[NavigationFailureType["cancelled"] = 8] = "cancelled";
    NavigationFailureType[NavigationFailureType["duplicated"] = 16] = "duplicated";
})(NavigationFailureType || (NavigationFailureType = {}));
const originalPush = VueRouter.prototype.push;
const originalReplace = VueRouter.prototype.replace;
const cleanUpAfterRouterError = (router, setTrackedRoute) => {
    getStore().dispatch('app/updateRouteLoading', false);
    // Reset route tracking to current route as navigation failed.
    setTrackedRoute(router.currentRoute);
};
const reloadChunkFailedRoute = async (path) => {
    // Due to our server configuration, files that are not found are returned
    // as index.html. As the service worker has now cached index.html as the
    // response for the JS file that failed to load, we must unregister the
    // service worker for the correct JS file to be fetched on the reload.
    const registrations = await navigator.serviceWorker.getRegistrations();
    registrations.forEach((registration) => {
        registration.unregister();
    });
    window.location.href = path;
};
const handleChunkLoadingError = (error, path) => {
    var _a, _b;
    const isMissing = error.message.includes('missing:');
    const isError = error.message.includes('error:');
    // CSS failures do not include missing/error
    const isUnknown = !isMissing && !isError;
    const failedFilePath = (_b = (_a = error.message.match(/\(.+?(\/.+?)\)/)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : 'unknown path';
    console.info(`Unable to load chunk for ${path} at ${failedFilePath}
    version ${process.env.VUE_APP_VERSION}`);
    if (isMissing) {
        SnackbarProgrammatic.open({
            actionText: 'reload',
            message: 'There has been an update to CareSend. Please reload to continue.',
            type: 'is-primary',
            indefinite: true,
            position: 'is-top',
            onAction() { reloadChunkFailedRoute(path); },
        });
    }
    else if (isError || isUnknown) {
        toastError(`There was a problem loading the page. There may be a problem with your
      internet connection. ${errorSuffix}`);
    }
};
/**
 * Detects chunk loading related error messages.
 *
 * Example chunk loading error messages:
 * - `Loading chunk chunk-office-team failed. (missing/error: url.js)`
 * - `Loading CSS chunk chunk-office-team failed. (url.css)`
 */
const isChunkLoadingError = (error) => /Loading .*?chunk/.test(error.message);
const handleErrors = (resolve, reject, router, currentRoute, getRouteData, setTrackedRoute, markErrorsHandled) => (error) => {
    const routeData = getRouteData();
    if (routeData.errorsHandled)
        return;
    if (VueRouter.isNavigationFailure(error, NavigationFailureType.redirected)
        || VueRouter.isNavigationFailure(error, NavigationFailureType.duplicated)
        || VueRouter.isNavigationFailure(error, NavigationFailureType.aborted)
        || VueRouter.isNavigationFailure(error, NavigationFailureType.cancelled)) {
        markErrorsHandled();
        cleanUpAfterRouterError(router, setTrackedRoute);
        resolve(currentRoute);
    }
    else if (isChunkLoadingError(error)) {
        markErrorsHandled();
        handleChunkLoadingError(error, routeData.route.path);
        cleanUpAfterRouterError(router, setTrackedRoute);
        resolve(currentRoute);
    }
    else {
        reject(error);
    }
};

export { cleanUpAfterRouterError, handleChunkLoadingError, handleErrors, isChunkLoadingError, originalPush, originalReplace };
